import { useEffect, useMemo } from "react";
import { Add, Close, Remove } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { TOP_BAR_HEIGHT } from "../../../pages/AppLayout";
import { grey, indigo } from "../../../colors";

import useMarketResearchStore from "../useMarketResearchStore";
import DateRangeFilter from "./DateRangeFilter";
import FilterDrawerControls from "./FilterDrawerControls";

export const FILTER_WIDTH = 300;

type FacetValue = {
  label: string;
};

const numericFirstSort = (a: FacetValue, b: FacetValue): number => {
  const aStr = a.label;
  const bStr = b.label;

  const aStartsNum = /^\d/.test(aStr);
  const bStartsNum = /^\d/.test(bStr);

  // Numbers come before letters
  if (aStartsNum && !bStartsNum) return -1;
  if (!aStartsNum && bStartsNum) return 1;

  // Both are same type - use natural sort
  return aStr.localeCompare(bStr, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export default function FilterDrawer() {
  const {
    facetsArray,
    setFilter,
    hasFilters,
    hasUnappliedFilterChanges,
    applyFilters,
    syncDraftFromApplied,
    resetFilters,
    isLoadingFacets,
    toggleExpanded,
    isExpanded,
    isFacetChecked,
    drawer,
    toggleFilterDrawer,
    addDateRangeFilter,
    draftDateRange,
  } = useMarketResearchStore();

  const processedFacets = useMemo(() => {
    if (!facetsArray) return [];
    return facetsArray?.map((facet) => {
      const rawValues: string[] = facet.filter_values ?? [];

      const dataWithStrings: FacetValue[] = rawValues.map((value) => ({
        label: value,
      }));

      // Sort all items first, then partition into selected/unselected
      const sortedData = [...dataWithStrings].sort(numericFirstSort);

      const selected: FacetValue[] = [];
      const unselected: FacetValue[] = [];

      sortedData.forEach((item) => {
        if (isFacetChecked(facet.column_name, item.label)) {
          selected.push(item);
        } else {
          unselected.push(item);
        }
      });

      return {
        ...facet,
        sortedValues: [...selected, ...unselected],
      };
    });
  }, [facetsArray, isFacetChecked]);

  const showFilters = drawer.open;
  const onCloseClick = () => toggleFilterDrawer();

  // Seed the draft from the applied filters whenever the panel opens so staged
  // selections start from what is currently applied.
  useEffect(() => {
    if (showFilters) syncDraftFromApplied();
  }, [showFilters]);

  return (
    <Drawer
      open={showFilters}
      sx={{
        width: showFilters ? FILTER_WIDTH : 0,
        "& .MuiDrawer-paper": {
          width: FILTER_WIDTH,
          boxSizing: "border-box",
          borderRight: `1px solid ${grey[100]}`,
          backgroundColor: "#FFFFFF",
        },
      }}
      variant="persistent"
      anchor="left"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          paddingTop: `${TOP_BAR_HEIGHT + 16}px`,
        }}
      >
        <Close onClick={onCloseClick} sx={{ position: "absolute", right: 20, display: "none" }} />

        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: indigo[900],
            px: 2,
            pb: 1,
          }}
        >
          Filters
        </Typography>

        <Box sx={{ flex: 1, overflowY: "auto", px: 0.5 }}>
          {isLoadingFacets ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box sx={{ padding: "10px 12px" }}>
                <DateRangeFilter
                  dateRange={draftDateRange}
                  onDateRangeSelected={(range) => addDateRangeFilter(range)}
                />
              </Box>
              <Divider sx={{ mt: "5px", borderColor: grey[100] }} />

              {processedFacets?.map((filter, i) => (
                <Box key={i}>
                  <Accordion
                    sx={{
                      background: "transparent",
                      boxShadow: "none",
                      "&:before": { display: "none" },
                    }}
                    elevation={0}
                    expanded={isExpanded(filter.column_name)}
                    onChange={() => toggleExpanded(filter.column_name)}
                  >
                    <AccordionSummary
                      expandIcon={
                        isExpanded(filter.column_name) ? (
                          <Remove sx={{ fontSize: 18, color: indigo[900] }} />
                        ) : (
                          <Add sx={{ fontSize: 18, color: indigo[900] }} />
                        )
                      }
                      sx={{
                        px: 1.5,
                        borderRadius: "8px",
                        minHeight: 44,
                        "&:hover": { backgroundColor: grey[100] },
                        "& .MuiAccordionSummary-content": { my: 1 },
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: indigo[900] }}>
                        {filter.filter_name}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
                      {filter?.sortedValues?.map(({ label }) => (
                        <Box
                          key={label}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isFacetChecked(filter.column_name, label)}
                                onChange={(event) => {
                                  setFilter(filter.column_name, label, event.target.checked);
                                }}
                                sx={{ color: grey[300], "&.Mui-checked": { color: indigo[700] } }}
                              />
                            }
                            label={<Typography sx={{ fontSize: 13, color: grey[700] }}>{label}</Typography>}
                          />
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                  <Divider sx={{ borderColor: grey[100] }} />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            backgroundColor: "white",
            borderTop: `1px solid ${grey[100]}`,
            zIndex: 10,
          }}
        >
          <FilterDrawerControls
            hasActiveFilters={hasFilters()}
            hasUnappliedChanges={hasUnappliedFilterChanges()}
            onClickReset={() => resetFilters()}
            onClickApply={() => applyFilters()}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
